import { PageHeader } from '@ant-design/pro-layout'
import { useSuspenseQuery } from '@apollo/client'
import { FetchUserActiviesDocument } from '@salescore/client-api'
import { CONSTANT } from '@salescore/client-base'
import { getOrganizationIdFromPath } from '@salescore/client-common'
import { ActivitiesTable, ActivityChart } from '@salescore/client-feature-setting'
import { CsvDownloadButton, datetimeFormat } from '@salescore/frontend-common'
import { createFileRoute } from '@tanstack/react-router'
import { Card, DatePicker, Space } from 'antd'
import dayjs, { type Dayjs } from 'dayjs'
import { Array, Order, pipe } from 'effect'
import { type ReactNode, useState } from 'react'
import { useTranslation } from 'react-i18next'

interface DateRange {
  startAt: Dayjs
  endAt: Dayjs
}

const Page = (): ReactNode => {
  const { t } = useTranslation()
  const [dateRange, setDateRange] = useState({
    startAt: getDateRangeStartAt(),
    endAt: getDateRangeEndAt(),
  })

  const { data } = useSuspenseQuery(FetchUserActiviesDocument, {
    variables: {
      organizationId: getOrganizationIdFromPath(),
      startAt: dateRange.startAt.toISOString(),
      endAt: dateRange.endAt.toISOString(),
    },
  })

  const dataSets = data.userActivities.map((activity) => ({
    userName: activity.user.name,
    loginCount: activity.loginCount,
    session: activity.session,
  }))

  const sortByLoginCountDataSets = pipe(
    dataSets,
    Array.sortBy(Order.mapInput(Order.Number, (dataSet) => dataSet.loginCount)),
  )
  const sortBySessionDataSets = pipe(dataSets, Array.sortBy(Order.mapInput(Order.Number, (dataSet) => dataSet.session)))

  return (
    <>
      <PageHeader
        title={
          <>
            <span>{t(`利用状況`)}</span>
            <Space />
            <DatePicker.RangePicker
              style={{ marginLeft: '8px' }}
              defaultValue={[dateRange.startAt, dateRange.endAt]}
              onChange={(value) => {
                setDateRange({
                  startAt: getDateRangeStartAt(value?.[0]),
                  endAt: getDateRangeEndAt(value?.[1]),
                })
              }}
            />
          </>
        }
        extra={[
          <CsvDownloadButton
            key={1}
            data={data.userActivities.map((x) => [
              x.user.name,
              x.user.identity.email,
              x.loginCount,
              datetimeFormat(x.lastSignedInAt),
              x.viewSheetDaysCount,
              x.viewKpiOrKpiPivotDaysCount,
            ])}
            filename={`${t(`利用状況_{{startAt}}_{{endAt}}`, {
              startAt: dateRange.startAt.format('YYYYMMDD'),
              endAt: dateRange.endAt.format('YYYYMMDD'),
            })}.csv`}
            headers={[
              t(`ユーザー名`),
              t(`メールアドレス`),
              t(`ログイン回数`),
              t(`最終ログイン日時`),
              t(`SYNC利用日数`),
              t(`VISUALIZE利用日数`),
            ]}
            disabled={data.userActivities.length === 0}
          />,
        ]}
      >
        <Card className="mb-3">
          <ActivitiesTable activities={data.userActivities} />
        </Card>

        <ActivityChart
          title={t(`ログイン日数`)}
          color={CONSTANT.colors.primaryColor}
          dataset={{
            userNames: sortByLoginCountDataSets.map((dataSet) => dataSet.userName),
            values: sortByLoginCountDataSets.map((dataSet) => dataSet.loginCount),
          }}
        />

        <div style={{ marginTop: '8px' }}></div>

        <ActivityChart
          title={t(`ログイン時間（分）`)}
          color={CONSTANT.colors.primaryColor}
          dataset={{
            userNames: sortBySessionDataSets.map((dataSet) => dataSet.userName),
            values: sortBySessionDataSets.map((dataSet) => dataSet.session),
          }}
        />
      </PageHeader>
    </>
  )
}

function getDateRangeStartAt(date?: Dayjs | null): Dayjs {
  return (date ?? dayjs().subtract(1, 'month')).startOf('d')
}

function getDateRangeEndAt(date?: Dayjs | null): Dayjs {
  return (date ?? dayjs()).endOf('d')
}

export const Route = createFileRoute('/o/$organizationId/v2/settings/user_activities')({
  component: Page,
})
